import type { CSSProperties, ReactNode } from "react";
import styles from "./Skeleton.module.css";

/**
 * The shared skeleton primitive — a placeholder shaped like the content it
 * stands in for, shown only while that content is genuinely on its way.
 *
 * ── FOUR PIECES, COMPOSED PER PAGE ───────────────────────────────────────────
 * `Skeleton` is the accessible wrapper; `SkeletonText`, `SkeletonBlock` and
 * `SkeletonRow` are the bars themselves. There is no page-specific skeleton
 * exported from here — a board's skeleton and a table's skeleton are shaped
 * too differently for one component to own both, so each caller composes its
 * own layout out of these three and wraps it in `Skeleton`. What is shared is
 * the bar (the shimmer, the colour, the reduced-motion fallback) and the
 * accessibility contract, not the shape.
 *
 * ── WHY ONE ANNOUNCEMENT, NOT ONE PER BAR ────────────────────────────────────
 * A skeleton with six bars and no `aria-hidden` reads to a screen reader as six
 * empty, unlabelled elements — noise with no content. `Skeleton` hides all of
 * that (`aria-hidden="true"` on the bars) and states the one true fact instead,
 * once, in a visually hidden line: "Loading <label>". `aria-busy="true"` on the
 * wrapper is the second half of that contract — it tells assistive tech this
 * region is mid-update without forcing a live-region announcement on every bar
 * inside it.
 *
 * ── WHAT THIS IS NOT FOR ──────────────────────────────────────────────────────
 * A genuine PENDING state only. Never render this for an error or an empty
 * result — see the callers, every one of which keeps its error and empty
 * branches rendering exactly as they did before this existed. A skeleton that
 * shows forever because a query failed is the bug that hides an outage.
 *
 * ── SHAPE STABILITY ───────────────────────────────────────────────────────────
 * Every composed skeleton is built to the same row count, column widths and
 * heights as the real content it precedes, so the layout does not jump when
 * data lands. Where the real length is unknown (a list with no fixed size), the
 * caller picks a sensible fixed count — around six rows — rather than
 * rendering nothing or an unbounded number of bars.
 */
export function Skeleton({
  label,
  children,
  className,
}: {
  /** What is loading, e.g. "the board". Read to screen readers as
      "Loading the board." — the one sentence a skeleton is allowed to say. */
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(styles.wrap, className)} aria-busy="true">
      <span className="sr-only">{`Loading ${label}.`}</span>
      {/* The bars are decorative. A screen reader has already heard the one
          honest sentence above; walking six empty boxes after it would be a
          screen reader user re-reading "loading" six times over. */}
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

/** One line of text, at roughly the width the real line will fill. Height is
    in em so it tracks whatever font-size the caller's context sets. */
export function SkeletonText({
  width = "100%",
  className,
}: {
  width?: string | number;
  className?: string;
}) {
  const style: CSSProperties = { width };
  return <span className={cx(styles.bar, styles.text, className)} style={style} />;
}

/** A rectangular block — a card, a chart, a tile. Width and height should
    match the real element's so the page does not reflow when it arrives. */
export function SkeletonBlock({
  width = "100%",
  height,
  className,
}: {
  width?: string | number;
  height?: string | number;
  className?: string;
}) {
  const style: CSSProperties = { width, height };
  return <div className={cx(styles.bar, styles.block, className)} style={style} />;
}

/** One row's worth of cells — a table row or a list item — each sized to the
    real column it stands in for. */
export function SkeletonRow({
  widths,
  className,
}: {
  /** One width per cell, in reading order. */
  widths: (string | number)[];
  className?: string;
}) {
  return (
    <div className={cx(styles.row, className)}>
      {widths.map((w, i) => (
        // Index as key is fine here: a fixed, ordered set of placeholders that
        // is never reordered or filtered while mounted.
        <SkeletonText key={i} width={w} />
      ))}
    </div>
  );
}

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}
